/**
 * @fileoverview Генератор ключей для mention
 * @description Утилиты для формирования и парсинга ключей mention
 */

import {
  MENTION_KEY_SEPARATOR,
  MENTION_DISPLAY_PREFIX,
  SPECIAL_SERVICE_CODES,
} from "./constants";

/**
 * Формирует displayKey для mention
 * @param {string} service_code - Код сервиса
 * @param {string} key - Ключ поля
 * @param {string} [participant_type_code] - Код участника
 * @returns {string} Сформированный displayKey
 *
 * @example
 * generateDisplayKey('gbdfl', 'first_name', 'supruga')
 * // => 'gbdfl_first_name_supruga'
 *
 * @example
 * generateDisplayKey('notary', 'fio')
 * // => 'notary_fio'
 */
export function generateDisplayKey(service_code, key, participant_type_code) {
  if (!service_code || !key) {
    console.warn("generateDisplayKey: missing required parameters", {
      service_code,
      key,
    });
    return key || "";
  }

  const parts = [service_code, key];

  // Для нотариуса и некоторых спец. сервисов не добавляем participant_type_code
  const skipParticipant =
    service_code === SPECIAL_SERVICE_CODES.NOTARY ||
    service_code === SPECIAL_SERVICE_CODES.MANUAL_INPUT;

  if (participant_type_code && !skipParticipant) {
    parts.push(participant_type_code);
  }

  return parts.join(MENTION_KEY_SEPARATOR);
}

/**
 * Парсит displayKey на составные части
 * @param {string} displayKey - Полный ключ mention
 * @returns {import('./types').MentionKeyComponents} Компоненты ключа
 *
 * @example
 * parseDisplayKey('gbdfl_first_name_supruga')
 * // => { service_code: 'gbdfl', key: 'first_name', participant_type_code: 'supruga' }
 */
export function parseDisplayKey(displayKey) {
  if (!displayKey || typeof displayKey !== "string") {
    return { service_code: "", key: "", participant_type_code: "" };
  }

  const parts = displayKey.split(MENTION_KEY_SEPARATOR);

  if (parts.length < 2) {
    return { service_code: "", key: displayKey, participant_type_code: "" };
  }

  const [service_code, ...rest] = parts;

  // Для нотариуса: notary_fio -> service_code=notary, key=fio
  if (service_code === SPECIAL_SERVICE_CODES.NOTARY) {
    return {
      service_code,
      key: rest.join(MENTION_KEY_SEPARATOR),
      participant_type_code: "",
    };
  }

  // Для обычных сервисов: gbdfl_first_name_supruga
  // service_code=gbdfl, key=first_name, participant_type_code=supruga
  if (parts.length === 3) {
    return {
      service_code: parts[0],
      key: parts[1],
      participant_type_code: parts[2],
    };
  }

  // Если больше 3 частей, последняя - participant_type_code
  const participant_type_code = parts[parts.length - 1];
  const key = parts.slice(1, -1).join(MENTION_KEY_SEPARATOR);

  return { service_code, key, participant_type_code };
}

/**
 * Добавляет префикс $ к ключу для отображения
 * @param {string} key - Ключ mention
 * @returns {string} Ключ с префиксом
 *
 * @example
 * formatDisplayKey('gbdfl_first_name_supruga')
 * // => '$gbdfl_first_name_supruga'
 */
export function formatDisplayKey(key) {
  if (!key) return "";
  return `${MENTION_DISPLAY_PREFIX}${key}`;
}

/**
 * Убирает префикс $ из ключа
 * @param {string} formattedKey - Ключ с префиксом
 * @returns {string} Ключ без префикса
 *
 * @example
 * removeDisplayPrefix('$gbdfl_first_name_supruga')
 * // => 'gbdfl_first_name_supruga'
 */
export function removeDisplayPrefix(formattedKey) {
  if (!formattedKey) return "";
  return formattedKey.startsWith(MENTION_DISPLAY_PREFIX)
    ? formattedKey.slice(1)
    : formattedKey;
}

/**
 * Проверяет, является ли ключ специальным (notary, manual_input и т.д.)
 * @param {string} service_code - Код сервиса
 * @returns {boolean} true, если сервис специальный
 */
export function isSpecialServiceCode(service_code) {
  return Object.values(SPECIAL_SERVICE_CODES).includes(service_code);
}

/**
 * Создает уникальный идентификатор для mention узла
 * @param {import('./types').MentionAttributes} attrs - Атрибуты mention
 * @returns {string} Уникальный ID
 */
export function createMentionUID(attrs) {
  const { service_code, key, participant_type_code } = attrs;
  return generateDisplayKey(service_code, key, participant_type_code);
}
